import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { clearTokens, hasSession, setTokens, type TokenPair } from '../api/auth.js';
import { fetchAccount, logoutSession, type AccountProfile } from '../api/queries/session.js';

/**
 * `BR-1.1`'s six, plus TeraAgo's three (M046).
 *
 * One account may hold capabilities from both products — the same reason
 * `BR-1.2` lets one account be both `customer` and `owner`. The console
 * distinguishes the two products by capability, never by URL prefix (§4.1).
 */
export type Capability =
  | 'customer'
  | 'owner'
  | 'operator'
  | 'admin'
  | 'support'
  | 'finance'
  | 'cl_customer'
  | 'cl_driver'
  | 'cl_admin'
  | 'cl_company';

interface AuthContextValue {
  account: AccountProfile | null;
  /** True until the initial session restore settles. */
  loading: boolean;
  /**
   * Sign in, and hand back WHO just signed in.
   *
   * It returns the account rather than only setting it because the caller's
   * next act is usually to decide where to send them, and the capability that
   * decides it arrives with this request. Reading `has()` straight after an
   * `await signIn(...)` would read the closure captured before the account
   * landed — a driver routed to the dispatcher's console on the strength of a
   * stale `false`.
   */
  signIn: (pair: TokenPair) => Promise<AccountProfile | null>;
  logout: () => Promise<void>;
  /**
   * Read the account again. After registering a company or a driver the
   * server has granted a capability this session has not seen yet.
   */
  refresh: () => Promise<AccountProfile | null>;
  has: (capability: Capability) => boolean;
  hasAny: (capabilities: Capability[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [account, setAccount] = useState<AccountProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (): Promise<AccountProfile | null> => {
    if (!hasSession()) {
      setAccount(null);
      setLoading(false);
      return null;
    }
    try {
      const profile = await fetchAccount();
      setAccount(profile);
      return profile;
    } catch {
      // A refresh token that no longer works is not an error state — it is a
      // signed-out user. The client already cleared what it could.
      clearTokens();
      setAccount(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const signIn = useCallback(
    async (pair: TokenPair): Promise<AccountProfile | null> => {
      setTokens(pair);
      setLoading(true);
      return load();
    },
    [load],
  );

  const logout = useCallback(async () => {
    try {
      await logoutSession();
    } catch {
      // Signing out locally must succeed even if the server call does not.
    }
    /*
      `clearTokens` NOTIFIES, and `App.tsx` listens.

      The cached server data has to go with the session: the TeraAgo query keys
      are `['cityload', ...]` with no account in them, so the next person to sign
      in at a shared desk would be painted the previous operator's bookings,
      wallets and driver details from cache before their own refetch landed.
      Both shells say in their own comments that this must not happen.

      The clearing lives next to the query client rather than here, because
      there is more than one way to lose a session: this button, and a refresh
      token that stops working. Hanging it off the token store catches both,
      and keeps this provider from requiring a `QueryClient` that most of its
      callers have no other reason to supply.
    */
    clearTokens();
    setAccount(null);
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const capabilities = new Set(account?.capabilities ?? []);
    return {
      account,
      loading,
      signIn,
      logout,
      refresh: load,
      has: (capability) => capabilities.has(capability),
      hasAny: (list) => list.some((capability) => capabilities.has(capability)),
    };
  }, [account, loading, signIn, logout, load]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}

/**
 * The session, where having one is a nicety rather than a requirement.
 *
 * `useAuth` throws outside the provider, and rightly so: a page that reads a
 * capability and silently got `null` would show the wrong product. A page that
 * only wants the account holder's NAME for a greeting has no such stake — the
 * fallback is a greeting without a name, which is a perfectly good greeting.
 *
 * Presentation only. Anything that GATES on a capability keeps using `useAuth`,
 * so a missing provider stays a crash rather than becoming a leak.
 */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
